import { get } from 'lodash';

import API from '../../services/API';
import {
  selectAuthenticated,
  selectSignUpProperty,
  selectSignUpUser,
} from './selectors';
import { statusHandler } from '../../services/services';
import { updateSignupData } from '../persistedSignupData/actions';
import { persistor } from '..';

export const AUTH_LOGIN_START = 'AUTH_LOGIN_START';
export const AUTH_LOGIN_SUCCESS = 'AUTH_LOGIN_SUCCESS';
export const AUTH_LOGIN_ERROR = 'AUTH_LOGIN_ERROR';
export const AUTH_NETWORK_ERROR = 'AUTH_NETWORK_ERROR';

export const AUTH_SESSION = 'AUTH_SESSION';

export const AUTH_LOGOUT = 'AUTH_LOGOUT';

export const AUTH_SIGNUP = 'AUTH_SIGNUP';
export const AUTH_SIGNUP_TYPE = 'AUTH_SIGNUP_TYPE';
export const AUTH_SIGNUP_ROUTE = 'AUTH_SIGNUP_ROUTE';
export const AUTH_SIGNUP_COMPLETE = 'AUTH_SIGNUP_COMPLETE';
export const AUTH_SIGNUP_INVITE = 'AUTH_SIGNUP_INVITE';
export const AUTH_SIGNUP_USER = 'AUTH_SIGNUP_USER';
export const AUTH_SIGNUP_PROPERTY = 'AUTH_SIGNUP_PROPERTY';
export const AUTH_SIGNUP_RESET = 'AUTH_SIGNUP_RESET';

export const getAuthToken = () => {
  const cookie = document.cookie;
  return cookie.split('qm.jwt=')[1].split(';')[0];
};

export const setAuthCookie = token => {
  const domain = new URL(window.location);
  const hostname = domain.hostname.replace(/^(.*\.)?www\./, '');
  try {
    // set cookie for jwt in new web app
    document.cookie = 'qm.jwt=' + token + '; domain=' + hostname + '; path=/;';
  } catch (e) {}
};

export const authLogin = (
  username,
  password,
  authOptions = {},
  callback = () => {},
) => dispatch => {
  // look for provider here
  if (!username || !password) {
    dispatch({
      type: AUTH_LOGIN_ERROR,
      payload: {
        message: 'Please enter an username and password',
      },
    });
    return Promise.reject();
  }

  dispatch({
    type: AUTH_LOGIN_START,
  });

  const payload = {
    email: username,
    password,
    authOptions,
  };

  // mock async
  return API.api
    .post('/auth/session', payload)
    .then(statusHandler)
    .then(({ data }) => {
      callback();

      const userId = get(data, 'user.id', 0);
      const propertyId =
        get(data, 'user.default_property_id') ||
        get(data, 'user.properties.0.id', 0);

      API.setPropertyId(propertyId);
      API.setToken(data.token);
      setAuthCookie(data.token);

      dispatch({
        type: AUTH_LOGIN_SUCCESS,
        payload: {
          ...data,
          userId,
          propertyId,
          keep_invite: authOptions.keep_invite,
        },
      });
      return data;
    });
};

export const ssoLogin = data => dispatch => {
  const userId = get(data, 'user.id', 0);
  const propertyId =
    get(data, 'user.default_property_id') ||
    get(data, 'user.properties.0.id', 0);

  API.setPropertyId(propertyId);
  API.setToken(data.token);
  setAuthCookie(data.token);

  dispatch({
    type: AUTH_LOGIN_SUCCESS,
    payload: {
      ...data,
      userId,
      propertyId,
    },
  });
};

export const authLogout = (authOptions, callback = () => {}) => dispatch => {
  window.localStorage.clear();
  callback();
  API.reset();
  dispatch({
    type: AUTH_LOGOUT,
  });
  dispatch({
    type: AUTH_SIGNUP_RESET,
  });
  persistor.purge();
  return Promise.resolve();
};

export const authSession = () => (dispatch, getState) =>
  API.api
    .get('/auth/session')
    .then(statusHandler)
    .then(({ data }) => {
      if (getAuthToken() && selectAuthenticated(getState())) {
        dispatch({
          type: AUTH_SESSION,
          payload: data,
        });
        return data;
      }
      throw data;
    })
    .catch(err => {});

export const authSignUp = payload => dispatch =>
  API.api
    .post('/v2/signup', payload)
    .then(statusHandler)
    .then(({ data }) => {
      const property = data.related_property || data.user.properties[0];
      const propertyId = property ? property.id : 0;

      API.setToken(data.token);
      setAuthCookie(data.token);
      API.setPropertyId(propertyId);

      dispatch(
        updateSignupData({
          last_name: payload.last_name,
        }),
      );

      dispatch({
        type: AUTH_SIGNUP,
        payload: {
          ...data,
          property,
          propertyId,
        },
      });
      return data;
    });

export const authSignUpComplete = () => dispatch => {
  dispatch({
    type: AUTH_SIGNUP_COMPLETE,
  });
  return Promise.resolve();
};

export const getAuthInvite = (id, token, clear, query) => dispatch => {
  if (clear) {
    const qmAdminAuthData = window.localStorage.getItem('qm_aa.auth');
    const qmAdminToken = window.localStorage.getItem('qm_aa.token');
    const qmSavedState = window.localStorage.getItem('qm_aa.saved_states');

    window.localStorage.clear();
    if (qmAdminAuthData) {
      window.localStorage.setItem('qm_aa.auth', qmAdminAuthData);
    }
    if (qmAdminToken) {
      window.localStorage.setItem('qm_aa.token', qmAdminToken);
    }
    if (qmSavedState) {
      window.localStorage.setItem('qm_aa.saved_states', qmSavedState);
    }

    dispatch({
      type: AUTH_SIGNUP_RESET,
    });
  }
  return API.api
    .get(`/v2/invitation/${token}${query || ''}`)
    .then(statusHandler)
    .then(({ data }) => {
      const payload = { id, token, data };
      window.localStorage.setItem('qm.invitation', JSON.stringify(data));
      dispatch({
        type: AUTH_SIGNUP_INVITE,
        payload,
      });
      return payload;
    });
};

export const setSignUpType = (type, clear) => dispatch => {
  dispatch({
    type: AUTH_SIGNUP_TYPE,
    payload: { type, clear },
  });
  return Promise.resolve();
};

export const setSignUpRoute = route => dispatch => {
  dispatch({
    type: AUTH_SIGNUP_ROUTE,
    payload: route,
  });
  return Promise.resolve();
};

export const setAuthPropertyData = payload => dispatch => {
  dispatch({
    type: AUTH_SIGNUP_PROPERTY,
    payload,
  });
};

export const setAuthUserData = payload => dispatch => {
  dispatch({
    type: AUTH_SIGNUP_USER,
    payload,
  });
};

export const postAuthProperty = payload => dispatch =>
  API.api
    .post('/properties', payload)
    .then(statusHandler)
    .then(({ data }) => {
      dispatch({
        type: AUTH_SIGNUP_PROPERTY,
        payload: data,
      });
      return data;
    });

export const setPropertyConflictResolved = () => dispatch => {
  dispatch({
    type: AUTH_SIGNUP_PROPERTY,
    payload: {},
  });
  return Promise.resolve();
};

export const postAuthPropertyPhoto = payload => (dispatch, getState) =>
  API.api
    .post(`/properties/${selectSignUpProperty(getState()).id}/image`, payload)
    .then(statusHandler)
    .then(({ data }) => {
      dispatch({
        type: AUTH_SIGNUP_PROPERTY,
        payload: data,
      });
      return data;
    });

export const postAuthUserPhoto = payload => (dispatch, getState) =>
  API.api
    .post(`/users/${selectSignUpUser(getState()).id}`, payload)
    .then(statusHandler)
    .then(({ data }) => {
      dispatch({
        type: AUTH_SIGNUP_USER,
        payload: data,
      });
      return data;
    });

export const postAuthPropertyCheck = payload => dispatch =>
  API.api
    .post('/v2/signup/check_property', payload)
    .then(statusHandler)
    .then(({ data }) => {
      dispatch({
        type: AUTH_SIGNUP_PROPERTY,
        payload: data,
      });
      return data;
    });

export const showNetworkAlert = payload => dispatch => {
  dispatch({
    type: AUTH_NETWORK_ERROR,
    payload,
  });
  return Promise.resolve();
};
